import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { useFetchMutation, useFetchQuery } from "common/queries/hooks";

import { urls, keys } from "./constants";
import {
  CampaignJourneyCreate,
  ConsentPreferencesResponse,
  ConsentSource,
  ConsentSubmitPayload,
  ConsentSyncPayload,
} from "../types";

const noop = () => null;

export const useCreateCampaignJourney = () => {
  return useFetchMutation<CampaignJourneyCreate, null>({
    fetch: (body) => ({ method: "POST", url: urls.applyCampaignJourney(), body }),
  });
};

/**
 * Mutation hook for submitting consent preferences.
 * POSTs to the unified consent endpoint. Used by both banner and GPC flows.
 */
const useConsentMutation = ({
  onSuccess = noop,
}: {
  onSuccess?: () => void;
} = {}) => {
  const queryClient = useQueryClient();
  return useFetchMutation<ConsentSubmitPayload, unknown>({
    fetch: (body) => ({ method: "POST", url: urls.consentPreferences(), body }),
    config: {
      onSuccess: () => {
        queryClient.invalidateQueries(keys.consentPreferences());
        onSuccess();
      },
    },
  });
};

/**
 * Mutation hook for GPC (Global Privacy Control) consent.
 * Sends opt-out values (sell_share_opt_out, limit_sensitive_data true; accept_all_cookies false).
 */
export const useConsentGPCMutation = () => {
  const mutation = useConsentMutation();
  const { mutate } = mutation;

  const mutateGpc = useCallback(() => {
    mutate({
      source: ConsentSource.GPC,
      sell_share_opt_out: true,
      limit_sensitive_data: true,
      accept_all_cookies: false,
    });
  }, [mutate]);

  return {
    mutate: mutateGpc,
    isLoading: mutation.isLoading,
  };
};

/**
 * Mutation hook for banner consent preferences.
 * Transforms c15t consent format to backend field format.
 */
export const useConsentBannerMutation = () => {
  const mutation = useConsentMutation();
  const { mutate } = mutation;

  const mutateBanner = useCallback(
    (consent: ConsentSyncPayload) => {
      // Mapping of c15t categories to backend fields:
      // - experience → sell_share_opt_out (inverted: experience=true means toggle is OFF)
      // - functionality → limit_sensitive_data (inverted: functionality=true means toggle is OFF)
      // - marketing → accept_all_cookies (TRUE = marketing scripts allowed)
      mutate({
        source: ConsentSource.BANNER,
        sell_share_opt_out: !consent.experience,
        limit_sensitive_data: !consent.functionality,
        accept_all_cookies: consent.marketing,
      });
    },
    [mutate],
  );

  return {
    mutate: mutateBanner,
    isLoading: mutation.isLoading,
  };
};

/**
 * Hook to fetch consent preferences from our backend.
 * Fetches for both authenticated and anonymous users (session-based).
 */
export const useConsentPreferencesQuery = () => {
  return useFetchQuery<ConsentPreferencesResponse>(keys.consentPreferences(), {
    fetch: {
      url: urls.consentPreferences(),
    },
    config: {
      retry: false, // Don't retry on 404 (no consent record)
    },
  });
};

/**
 * Hook to detect Global Privacy Control (GPC) signal.
 * GPC is a browser-level privacy signal that indicates the user
 * has requested not to have their data sold or shared.
 *
 * This is handled entirely on the frontend via navigator.globalPrivacyControl.
 *
 * @see https://globalprivacycontrol.org/
 */
export const useGlobalPrivacyControl = (): boolean => {
  if (typeof navigator === "undefined") {
    return false;
  }
  const gpc = (navigator as { globalPrivacyControl?: boolean | string }).globalPrivacyControl;
  return gpc === true || gpc === "1";
};
